import React, { useEffect, useRef, useState } from "react";

import { get } from "../../api";
import { deviceTypeLabel } from "../../constants/sentinel_types";
import { useDebounce } from "../../hooks/inputs";
import getStatus from "../../helpers/statuses";
import Checkbox from "../Checkbox";
import Icon from "../Icon";
import TextInput from "../TextInput";

import styles from "./SerialPicker.module.scss";

/** Same floor as the API: fewer digits match too much to be useful. */
export const MIN_DIGITS = 3;
const RESULT_LIMIT = 20;
const DEBOUNCE_MS = 300;

/**
 * Finds devices by a fragment of their serial - the last digits off the label
 * are enough - and lets each match be ticked into a list. The list itself lives
 * with the parent: `selected` is the serials already in it and `onToggle` adds
 * or removes one, so the picker never disagrees with what is shown elsewhere.
 *
 * With `onPick` instead it picks one device: a click hands the whole match to
 * the parent and closes the list, and `selected` marks the one picked.
 */
export default function SerialPicker({ selected = [], onToggle, onPick, disabled = false, placeholder }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);

  const digits = text.replace(/\D/g, "");
  const query = useDebounce(digits, DEBOUNCE_MS);

  useEffect(() => {
    let cancelled = false;

    if (query.length < MIN_DIGITS) {
      setSummary(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    get("/devices/usage/search", { digits: query, limit: RESULT_LIMIT }).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res === undefined || !Array.isArray(res.results)) {
        setError("Could not search devices. Try again.");
        setSummary(null);
        return;
      }
      setSummary(res);
    });

    return () => {
      cancelled = true;
    };
  }, [query]);

  /* Click outside or Escape closes the list; the typed digits stay for the next look. */
  useEffect(() => {
    if (!open) return undefined;

    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selectedSet = new Set(selected.map((s) => String(s)));
  const results = (summary && summary.results) || [];
  const showList = open && digits.length >= MIN_DIGITS;

  function highlight(serial) {
    const text = String(serial);
    const at = text.lastIndexOf(query);
    if (at < 0) return text;
    return (
      <>
        {text.slice(0, at)}
        <mark className={styles.mark}>{text.slice(at, at + query.length)}</mark>
        {text.slice(at + query.length)}
      </>
    );
  }

  function toggle(device) {
    if (onPick) {
      onPick(device);
      setOpen(false);
      return;
    }
    const serial = String(device.serial_number);
    onToggle(serial, !selectedSet.has(serial));
  }

  function renderResults() {
    if (loading && !summary) {
      return <div className={styles.note}>Searching…</div>;
    }
    if (error) {
      return <div className={styles.noteError}>{error}</div>;
    }
    if (!summary) {
      return null;
    }
    if (results.length === 0) {
      return (
        <div className={styles.note}>
          No device serial contains <strong>{query}</strong>.
        </div>
      );
    }

    return (
      <>
        <ul className={styles.list}>
          {results.map((device) => {
            const serial = String(device.serial_number);
            const checked = selectedSet.has(serial);
            const status = device.status ? getStatus(device.status) : null;

            return (
              <li
                key={device.device_id}
                className={[styles.item, checked ? styles.itemChecked : ""].filter((c) => c).join(" ")}
                onClick={() => toggle(device)}
              >
                {!onPick && (
                  <div className={styles.check} onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={checked} onChange={() => toggle(device)} />
                  </div>
                )}
                <div className={styles.main}>
                  <div className={styles.serial}>
                    {highlight(serial)}
                    {device.suffix_match && (
                      <span className={styles.suffix} title="Serial ends with these digits">
                        ends with
                      </span>
                    )}
                  </div>
                  <div className={styles.meta}>
                    {device.name && device.name !== serial && <span>{device.name}</span>}
                    <span>{deviceTypeLabel(device.devicetype_id) || "Unknown type"}</span>
                    {status && (
                      <span className={styles.status}>
                        <span className={styles.dot} style={{ backgroundColor: status.colors[0] }} />
                        {status.name}
                      </span>
                    )}
                    <span className={device.active ? styles.active : styles.inactive}>
                      {device.active ? "Active" : "Inactive"}
                    </span>
                    {device.customer_name && <span>{device.customer_name}</span>}
                  </div>
                </div>
                {checked && <span className={styles.added}>{onPick ? "Showing" : "In list"}</span>}
              </li>
            );
          })}
        </ul>
        {summary.total > results.length && (
          <div className={styles.note}>
            Showing {results.length} of {summary.total} matches - type more digits to narrow it down.
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.picker} ref={wrapRef}>
      <TextInput
        icon="search"
        value={text}
        placeholder={placeholder || "Find a device by the last digits of its serial…"}
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        loading={loading}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        aria-label="Find a device by serial digits"
      />
      {open && digits.length > 0 && digits.length < MIN_DIGITS && (
        <div className={styles.dropdown}>
          <div className={styles.note}>Type at least {MIN_DIGITS} digits.</div>
        </div>
      )}
      {showList && <div className={styles.dropdown}>{renderResults()}</div>}
    </div>
  );
}
